import React, { useState } from 'react';
import { Heart, Star, Compass, ExternalLink, X } from 'lucide-react';
import { Card } from './ui/card';

const Legacy = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const stories = [
    {
      icon: Star,
      title: "My Grandfather's Legacy",
      type: 'modal',
      content: "A story of sacrifice, remembrance, and the promise that drives the World Peace Now movement.",
      accent: 'from-[#D4AF37]/10 to-[#0B1D2A]/80'
    },
    {
      icon: Heart,
      title: "A Father's Gift",
      type: 'external',
      url: 'http://bit.ly/15Septmber2000',
      content: "Through decades of quiet service, mentoring young people, and showing up for his community.",
      accent: 'from-[#D4AF37]/10 to-[#0B1D2A]/80'
    },
    {
      icon: Compass,
      title: "The Path of Transformation",
      type: 'external',
      url: 'http://bit.ly/landmarkworldwidealbum',
      content: "Through Landmark Education, discovering that the conversations we have with ourselves determine the life we live.",
      accent: 'from-[#D4AF37]/10 to-[#0B1D2A]/80'
    }
  ];

  const handleCardClick = (story) => {
    if (story.type === 'modal') {
      setIsModalOpen(true);
    } else if (story.type === 'external') {
      window.open(story.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <section id="legacy" className="py-24 bg-gradient-to-b from-black to-[#0B1D2A] relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2
              className="text-4xl sm:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              What <span className="text-[#D4AF37]">Matters</span>
            </h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto rounded-full mb-6" />
            <p
              className="text-lg text-white/70 max-w-2xl mx-auto"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              The foundations that shaped a life dedicated to transformation and peace
            </p>
          </div>

          {/* Story Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {stories.map((story, index) => (
              <Card
                key={index}
                onClick={() => handleCardClick(story)}
                className={`bg-gradient-to-br ${story.accent} border-[#D4AF37]/30 p-8 hover:border-[#D4AF37] transition-all duration-300 hover:transform hover:scale-105 backdrop-blur-sm group cursor-pointer relative`}
              >
                {story.type === 'external' && (
                  <div className="absolute top-4 right-4">
                    <ExternalLink className="w-5 h-5 text-[#D4AF37]/60 group-hover:text-[#D4AF37] transition-colors" />
                  </div>
                )}
                <div className="flex flex-col items-center text-center space-y-6">
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center group-hover:bg-[#D4AF37]/30 transition-colors">
                    <story.icon className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3
                    className="text-2xl font-bold text-white"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {story.title}
                  </h3>
                  <p
                    className="text-white/80 leading-relaxed"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  >
                    {story.content}
                  </p>
                </div>
              </Card>
            ))}
          </div>

          {/* Legacy Quote */}
          <div className="mt-16 text-center">
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <div className="absolute -top-6 -left-6 text-6xl text-[#D4AF37]/20">"</div>
                <p
                  className="text-2xl md:text-3xl font-semibold text-white/90 italic relative z-10"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  The legacy we leave isn't measured in accomplishments,
                  <br />
                  but in the <span className="text-[#D4AF37]">lives we touch</span> and the
                  <br />
                  <span className="text-[#D4AF37]">conversations we transform</span>.
                </p>
                <div className="absolute -bottom-6 -right-6 text-6xl text-[#D4AF37]/20">"</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grandfather's Legacy Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-[#0B1D2A] border-2 border-[#D4AF37]/30 rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto relative animate-slideUp">
            {/* Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="sticky top-4 right-4 float-right z-10 w-10 h-10 rounded-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 flex items-center justify-center transition-colors"
            >
              <X className="w-6 h-6 text-black" />
            </button>

            {/* Modal Content */}
            <div className="p-8 md:p-12">
              {/* Title */}
              <h2
                className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-8 text-center"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                A Silent Promise
              </h2>

              {/* Hero Image */}
              <div className="mb-8 rounded-lg overflow-hidden border-2 border-[#D4AF37]/20">
                <img
                  src="https://wp2020.com/uploads/3/6/1/9/36195236/7446360.jpg"
                  alt="Sandakan Massacre Memorial"
                  className="w-full h-auto"
                />
              </div>

              {/* Story Text */}
              <div
                className="text-white/90 text-lg leading-relaxed space-y-6 mb-8"
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                <p>
                  I MADE A SILENT PROMISE HERE IN 2005 (pictured) to my slain grandfather 'Ng Se Wah' (吳社華) on my mother's side, who was one of those remembered with this Sandakan Massacre Memorial; which commemorates 30 Chinese victims, most of them members of a local elite underground movement, who were executed on 27 May 1945 along with several other victims during the Japanese Occupation of North Borneo. The memorial was built on the spot where the massacre took place and where the victims were buried. It is located near a Chinese cemetery on a hill above the old town centre of Sandakan.
                </p>
                <p>
                  My English father was aged 8-14 when WWII German bombers heavily bombed Gt Yarmouth, where he lived with his mother and father, so he was sent north to safer York, to stay with his Auntie (tears galore indeed) and my Chinese mother had her home taken over by the occupying Japanese Army so her life at age 5-11 was spent in hardship and then the devastation of having her father shot at the end of World War II.
                </p>
                <p>
                  I participated in the Landmark Forum in 1993 and undertook their training and development courses for the next decade. It gave me the access to see one of my purposes in life: Can I stop the world fighting for one day as a GIFT to my mother and father, who endured a horrible war, where there was little time for such important things like LOVE and FAMILY.
                </p>
              </div>

              {/* Beach Image */}
              <div className="mb-6 rounded-lg overflow-hidden border-2 border-[#D4AF37]/20">
                <img
                  src="https://wp2020.com/uploads/3/6/1/9/36195236/2089059.jpg"
                  alt="Ocean Shores Beach, Charles Crawshaw"
                  className="w-full h-auto"
                />
              </div>

              {/* Beach Caption */}
              <p
                className="text-[#D4AF37] text-sm italic mb-4 text-center"
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                23 August 2016 — Ocean Shores Beach, New South Wales, Australia. Charles Crawshaw, born in the year of the rabbit, seen here being present to the current sadness of wars around the world today.
              </p>

              {/* Beach Description */}
              <div
                className="text-white/90 text-lg leading-relaxed space-y-6 mb-8"
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                <p>
                  Ocean Shores photographer Zandra Warland waited patiently for myself to become present to the sadness of war as seen above. I closed my eyes and pictured a photo I had seen many times on BBC and Al Jazeera News the previous fortnight — a harrowing image of a 5-year-old Syrian boy called Omran Daqneesh. The tears that eventually fell from my eyes came from knowing this little boy would eventually realise he had lost his brother Ali to the bombing of his home.
                </p>
              </div>

              {/* Third Image */}
              <div className="mb-8 rounded-lg overflow-hidden border-2 border-[#D4AF37]/20">
                <img
                  src="https://wp2020.com/uploads/3/6/1/9/36195236/4312713_orig.jpg"
                  alt="Memorial moment"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Legacy;
